import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Fetch } from '../../utils/Utils.js';
import ErrorMessage from '../../shared/ErrorMessage.js';
import LotProjectField from '../../shared/lots/LotProjectField.js';
import './WeightLots.css';

const METHODS = [
  { value: 'animal',      icon: '🐄', labelKey: 'tracking_method_animal',      descKey: 'tracking_method_animal_desc' },
  { value: 'lot_average', icon: '📋', labelKey: 'tracking_method_lot_average', descKey: 'tracking_method_lot_average_desc' },
  { value: 'score',       icon: '🎯', labelKey: 'tracking_method_score',       descKey: 'tracking_method_score_desc' },
  { value: 'combination', icon: '⚙️', labelKey: 'tracking_method_combination', descKey: 'tracking_method_combination_desc' },
];

export const AddWeightLot = ({ onSuccess }) => {
  const { t } = useTranslation();
  const [step,           setStep]           = useState(1);
  const [name,           setName]           = useState('');
  const [plans,          setPlans]          = useState([]);
  const [selectedPlanId, setSelectedPlanId] = useState('');
  const [trackingMethod, setTrackingMethod] = useState('');
  const [targetWeight,   setTargetWeight]   = useState('');
  const [adgEstimate,    setAdgEstimate]    = useState('');
  const [projectIds,     setProjectIds]     = useState([]);
  const [errorMessage,   setErrorMessage]   = useState('');

  useEffect(() => {
    Fetch('api/data/lots.plans?search_fields=type&search_values=weight', 'GET', null, (data) => setPlans(data || []));
    Fetch('api/data/account.settings', 'GET', null, (data) => {
      const id = data?.[0]?.default_weight_protocol_id;
      if (id) setSelectedPlanId(id);
    });
  }, []);

  const handleNext = (e) => {
    e.preventDefault();
    if (!name.trim()) return;
    setErrorMessage('');
    setStep(2);
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!trackingMethod) return;
    setErrorMessage('');
    const body = JSON.stringify({
      type: 'weight',
      name: name.trim(),
      protocol_id: selectedPlanId || null,
      tracking_method: trackingMethod,
      project_ids: projectIds,
      ...(trackingMethod === 'animal' && targetWeight ? { target_weight: parseFloat(targetWeight) } : {}),
      ...(trackingMethod === 'animal' && adgEstimate  ? { adg_estimate: parseFloat(adgEstimate) } : {}),
    });
    Fetch('api/data/lots', 'post', body, (lot) => {
      onSuccess(lot);
    }, () => {
      setErrorMessage(t('error_creating_weight_lot'));
    });
  };

  if (step === 1) {
    return (
      <form className="wl-add-form" onSubmit={handleNext}>
        <div className="wl-form-group">
          <label>{t('weight_lot_name')}</label>
          <input
            type="text"
            value={name}
            onChange={e => setName(e.target.value)}
            placeholder={t('weight_lot_name_placeholder')}
            maxLength={50}
          />
        </div>
        <LotProjectField value={projectIds} onChange={setProjectIds} groupClassName="wl-form-group" />
        <button type="submit" className="wl-submit-btn" disabled={!name.trim()}>{t('next_button')}</button>
        <ErrorMessage message={errorMessage} />
      </form>
    );
  }

  return (
    <form className="wl-add-form" onSubmit={handleSubmit}>
      <div className="wl-form-group">
        <label>{t('tracking_method_question')}</label>
      </div>
      <p className="wl-method-hint">{t('tracking_method_required_hint')}</p>
      <div className="wl-method-grid">
        {METHODS.map(m => (
          <div
            key={m.value}
            className={`wl-method-card${trackingMethod === m.value ? ' wl-method-card-active' : ''}`}
            onClick={() => setTrackingMethod(m.value)}
          >
            <div className="wl-method-card-icon">{m.icon}</div>
            <div className="wl-method-card-label">{t(m.labelKey)}</div>
            <div className="wl-method-card-desc">{t(m.descKey)}</div>
          </div>
        ))}
      </div>

      {trackingMethod && trackingMethod !== 'score' && (
        <div className="wl-form-group">
          <label>{t('lot_plan_optional')}</label>
          <select value={selectedPlanId} onChange={e => setSelectedPlanId(e.target.value)}>
            <option value="">-- {t('no_plan')} --</option>
            {plans.map(p => (
              <option key={p._id} value={p._id}>{p.name}</option>
            ))}
          </select>
        </div>
      )}

      {trackingMethod === 'animal' && (
        <>
          <div className="wl-form-group">
            <label>{t('target_weight_optional')}</label>
            <input
              type="number"
              step="0.1"
              min="0"
              value={targetWeight}
              onChange={e => setTargetWeight(e.target.value)}
              placeholder="530"
            />
          </div>
          <div className="wl-form-group">
            <label>{t('adg_estimate_optional')}</label>
            <input
              type="number"
              step="0.01"
              min="0"
              value={adgEstimate}
              onChange={e => setAdgEstimate(e.target.value)}
              placeholder="0.8"
            />
          </div>
        </>
      )}

      <div className="wl-step-actions">
        <button type="button" className="wl-back-btn" onClick={() => setStep(1)}>{t('back_button')}</button>
        <button type="submit" className="wl-submit-btn" disabled={!trackingMethod}>{t('add_button')}</button>
      </div>
      <ErrorMessage message={errorMessage} />
    </form>
  );
};

export default AddWeightLot;
